import { EPSTest } from './';
import { Either, Failure, Left, Right, isRight, Environment } from 'types';
import { CarModuleTest } from './car';
import { HotelModuleTest } from './hotel';
import { AviaModuleTest } from './avia';
import { HostModuleTest } from './host';
import { CurrencyModuleTest } from './currency';
import { LocaleModuleTest } from './locale';
import { FeaturesModuleTest } from './features';

export namespace Configurator {
  export const CONFIGURATOR_KEY = 'testable_persistance_data';
  export const VERSION = 31;

  export interface IPersistent {
    eps: EPSTest.EPSPersistance;
    carModule: CarModuleTest.Persistance;
    hotelModule: HotelModuleTest.Persistance;
    aviaModule: AviaModuleTest.Persistance;
    hostModule: HostModuleTest.Persistance;
    currencyModule: CurrencyModuleTest.Persistance;
    localeModule: LocaleModuleTest.Persistance;
    featuresModule: FeaturesModuleTest.Persistance;
    version: number;
  }

  export const builder = (): State => {
    return new _Configurator(); // TODO: remove after release
    let env = process.env.NODE_ENV || Environment.Development;

    if (env === Environment.Development || env === Environment.Stage) {
      return new _Configurator();
    } else {
      return null;
    }
  };

  export interface State {
    eps: EPSTest.Configurator;
    carModule: CarModuleTest.Configurator;
    hotelModule: HotelModuleTest.Configurator;
    aviaModule: AviaModuleTest.Configurator;
    hostModule: HostModuleTest.Configurator;
    currencyModule: CurrencyModuleTest.Configurator;
    localeModule: LocaleModuleTest.Configurator;
    featuresModule: FeaturesModuleTest.Configurator;
    store(): void;
  }

  class _Configurator implements State {
    eps: EPSTest.Configurator;
    carModule: CarModuleTest.Configurator;
    hotelModule: HotelModuleTest.Configurator;
    aviaModule: AviaModuleTest.Configurator;
    hostModule: HostModuleTest.Configurator;
    currencyModule: CurrencyModuleTest.Configurator;
    localeModule: LocaleModuleTest.Configurator;
    featuresModule: FeaturesModuleTest.Configurator;
    version: number;

    constructor() {
      const either = _Configurator.restore();
      this.eps = isRight(either)
        ? new EPSTest.Configurator(either.value.eps)
        : new EPSTest.Configurator();
      this.carModule = isRight(either)
        ? new CarModuleTest.Configurator(either.value.carModule)
        : new CarModuleTest.Configurator();

      this.hotelModule = isRight(either)
        ? new HotelModuleTest.Configurator(either.value.hotelModule)
        : new HotelModuleTest.Configurator();

      this.aviaModule = isRight(either)
        ? new AviaModuleTest.Configurator(either.value.aviaModule)
        : new AviaModuleTest.Configurator();

      this.hostModule = isRight(either)
        ? new HostModuleTest.Configurator(either.value.hostModule)
        : new HostModuleTest.Configurator();

      this.currencyModule = isRight(either)
        ? new CurrencyModuleTest.Configurator(either.value.currencyModule)
        : new CurrencyModuleTest.Configurator();

      this.localeModule = isRight(either)
        ? new LocaleModuleTest.Configurator(either.value.localeModule)
        : new LocaleModuleTest.Configurator();

      this.featuresModule = isRight(either)
        ? new FeaturesModuleTest.Configurator(either.value.featuresModule)
        : new FeaturesModuleTest.Configurator();

      this.version = VERSION;
    }

    static restore(): Either<Failure.Absract, IPersistent> {
      const json = localStorage.getItem(CONFIGURATOR_KEY);
      try {
        if (json == null) {
          throw new Error();
        }
        const parsed = JSON.parse(json) as IPersistent;
        if (!parsed.eps.headersCollection) {
          throw new Error();
        }
        if (parsed.version !== VERSION) {
          throw new Error();
        }
        return Right(parsed);
      } catch (e) {
        localStorage.removeItem(CONFIGURATOR_KEY);
        return Left(new Failure.StorageFailure());
      }
    }

    store() {
      localStorage.setItem(
        CONFIGURATOR_KEY,
        JSON.stringify(this.toPersistent()),
      );
    }

    private toPersistent(): IPersistent {
      return {
        eps: this.eps.toPersistance(),
        version: this.version,
        carModule: this.carModule,
        hotelModule: this.hotelModule,
        aviaModule: this.aviaModule,
        hostModule: this.hostModule,
        currencyModule: this.currencyModule,
        localeModule: this.localeModule,
        featuresModule: this.featuresModule,
      };
    }
  }
}
